import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimPriceAnalyticsItemFilterInput } from 'gql/graphql';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { bimPriceTableDataAnalyticsGql } from '../bimWorkOrderPriceAnalyticsQueries';
import type { TAnalyticsData, TFilters, TProps, TRow, TSimpleFilterFields } from '../types';

const createOrFilter = (values: string[]) => ({
    or: values.map((value) => ({ eq: value })),
});
const addToPriceFilter = (acc: BimPriceAnalyticsItemFilterInput, field: string, values: string[]) => {
    const filter = createOrFilter(values);

    if (!acc.price) {
        acc.price = { or: [{ refPriceValue: { and: [] } }, { topPriceValue: { and: [] } }] };
    }
    acc.price?.or?.[0]?.refPriceValue?.and?.push({ [field]: filter });
    acc.price?.or?.[1]?.topPriceValue?.and?.push({ [field]: filter });
};

const getQueryWhere = (filters: TFilters): BimPriceAnalyticsItemFilterInput | undefined => {
    const simpleFields = ['section', 'subsection'];

    const allEmpty = Object.values(filters ?? {}).every((value) => !value?.length);

    if (allEmpty) {
        return undefined;
    }

    return Object.entries(filters ?? {}).reduce((acc: BimPriceAnalyticsItemFilterInput, [field, values]) => {
        if (simpleFields.includes(field)) {
            (acc as any)[field] = values?.length
                ? {
                      or: values.reduce<TSimpleFilterFields[]>((valueAcc, i) => {
                          valueAcc.push(
                              {
                                  refHeaderValue: { name: { eq: i } },
                              },
                              {
                                  topHeaderValue: { name: { eq: i } },
                              },
                          );

                          return valueAcc;
                      }, []),
                  }
                : undefined;
        } else if (values?.length) {
            addToPriceFilter(acc, field, values);
        }

        return acc;
    }, {});
};

export const useAnalyticsData = ({ priceLevel, refWorkOrderPriceId, workOrderPriceId }: TProps) => {
    const [filtersWhere, setFiltersWhere] = useState<TFilters>(undefined);

    const changeFiltersHandler = useCallback(({ fieldName, checkedValues }) => {
        setFiltersWhere((prevValue) => ({
            ...prevValue,
            [fieldName]: checkedValues?.length ? checkedValues : undefined,
        }));
    }, []);

    const variables = useMemo(
        () => ({
            workOrderPriceId,
            refWorkOrderPriceId,
            priceLevel,
            where: getQueryWhere(filtersWhere),
            take: 1000,
        }),
        [filtersWhere, priceLevel, refWorkOrderPriceId, workOrderPriceId],
    );

    const withEffect = useMemo(
        () => workOrderPriceId !== undefined && refWorkOrderPriceId !== undefined,
        [refWorkOrderPriceId, workOrderPriceId],
    );

    const [data] = useRequest({
        requestId: 'bimPriceTableDataAnalytics',
        query: bimPriceTableDataAnalyticsGql,
        variables,
        withEffect,
    });

    const [resetData] = useResetStoreData({
        requestId: 'bimPriceTableDataAnalytics',
    });

    const tableData = useMemo(() => {
        const groupedDataObj = data?.bimPriceRowAnalytics?.items?.reduce((acc: TAnalyticsData, item) => {
            const { section, subsection, price } = item;

            const sectionId = section?.sysId as number;
            const subsectionId = subsection?.sysId as number;

            if (!acc[sectionId]) {
                acc[sectionId] = {
                    order: section.order,
                    rows: [
                        {
                            isSection: true,
                            status: section.status,
                            topPriceValue: {
                                section: item?.section?.topHeaderValue?.name,
                            },
                            refPriceValue: {
                                section: item?.section?.refHeaderValue?.name,
                            },
                            ...item,
                        },
                    ],
                };
            }

            if (subsectionId && subsection) {
                if (!acc[sectionId][subsectionId]) {
                    acc[sectionId][subsectionId] = {
                        order: subsection.order,
                        rows: [
                            {
                                isSubsection: true,
                                status: subsection.status,
                                topPriceValue: {
                                    subsection: item?.subsection?.topHeaderValue?.name,
                                },
                                refPriceValue: {
                                    subsection: item?.subsection?.refHeaderValue?.name,
                                },
                                ...item,
                            },
                            price,
                        ],
                    };
                } else {
                    acc[sectionId][subsectionId].rows.push(price);
                }
            } else {
                acc[sectionId]?.rows?.push(price);
            }

            return acc;
        }, {});

        const sortedGroups = Object.values(groupedDataObj ?? {}).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        return sortedGroups.reduce((acc: TRow[], group) => {
            const sortedSubsections = Object.values(group)
                .filter((i) => !Number.isInteger(i) && !Array.isArray(i))
                .sort((a: any, b: any) => a.order - b.order)
                .map((subsection: any) => subsection.rows.sort((a: any, b: any) => a.order - b.order))
                .flat();

            const sortedItemsWithoutSubsections =
                group.rows?.sort((a, b) => ('order' in a ? a.order : 0) - ('order' in b ? b.order : 0)) ?? [];

            acc.push(...sortedItemsWithoutSubsections, ...sortedSubsections);

            return acc;
        }, []);
    }, [data]);

    useEffect(
        () => () => {
            resetData(undefined);
        },
        [resetData],
    );

    return { tableData, changeFiltersHandler };
};
